/**
 * The flight screen: the orbital globe you fly into, and the tactical COP with
 * the controls that move aircraft.
 *
 * The map opens at ORBIT — a globe with every AOR shown as a point. Clicking an
 * AOR (or FLY INTO AOR) flies down into the tactical COP; RETURN TO ORBIT flies
 * back out. Level-of-detail (from zoom) gates which HUD shows: strategic shows
 * the AOR picker; tactical shows the fleet rail, right rail, and command dock.
 *
 * Three things in the dock are safety features rather than UI polish.
 *
 * Every command button is behind one re-entrancy guard held in a ref, not state:
 * two clicks in one frame both read the old value, and a double-tapped ARM sent
 * two arm commands. A fan-out reports one line per aircraft — "commanded 6" is a
 * lie when four refused. And DISARM asks again whenever the aircraft is armed and
 * off the ground, because that command is a controlled fall.
 */

import { Ban, Check, X } from 'lucide-react';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type CSSProperties,
  type ReactNode,
} from 'react';

import type { ApiResult } from '@/api/client';
import { droneCommands, fanOut } from '@/api/drones';
import { pauseMission, resumeMission, startMission } from '@/api/missions';
import { FORMATION_LABEL, formUp, type FormationKind } from '@/api/formation';
import { EmergencyControls } from '@/features/command/EmergencyControls';
import { RightRail } from '@/features/command/RightRail';
import { useAorCounts } from '@/features/fleet/aorScope';
import { FleetRail, teamColorFor } from '@/features/fleet/FleetRail';
import { isSurfaceVehicle } from '@/features/fleet/vehicleKind';
import {
  isValidTakeoffAltitude,
  loadTakeoffAltitude,
  saveTakeoffAltitude,
} from '@/features/command/takeoffAltitude';
import { BottomSheet, type SheetHeight } from '@/mobile/BottomSheet';
import { useIsMobile } from '@/mobile/useIsMobile';
import { useViewport } from '@/mobile/useViewport';
import { cn } from '@/lib/utils';
import { AORS, ORBIT, aorById, aorForPosition, type Aor } from '@/map/aors';
import {
  MapCanvas,
  createMapMarker,
  useMapController,
  type MapController,
  type MapLike,
} from '@/map/MapCanvas';
import { MapContextMenu, type MenuBuilder, type MenuItem } from '@/map/MapContextMenu';
import { CommandTargetHoverLabel, useCommandTargetLayer } from '@/map/useCommandTargetLayer';
import { useDroneMarkers } from '@/map/useDroneMarkers';
import { useSelectionInView } from '@/map/useSelectionInView';
import { useSimDrag } from '@/map/useSimDrag';
import { useMissionLayer } from '@/map/useMissionLayer';
import { useZoneLayer } from '@/map/useZoneLayer';
import { useOverlayLayer } from '@/map/useOverlayLayer';
import { useImpactLayer } from '@/map/useImpactLayer';
import { useThreatLayer } from '@/map/useThreatLayer';
import { AttackHoverLabel, useAttackLayer } from '@/map/useAttackLayer';
import { SupplySiteHoverLabel, useSupplyLayer } from '@/map/useSupplyLayer';
import { ExternalTrackHoverLabel, useExternalTrackLayer } from '@/map/useExternalTrackLayer';
import { SurroundingsDetailCard, useSurroundingsLayer } from '@/map/useSurroundingsLayer';
import { useCarrierLayer } from '@/map/useCarrierLayer';
import { useSortieTrackLayer } from '@/map/useSortieTrackLayer';
import { useThreatStore } from '@/stores/threatStore';
import { useSupplyStore } from '@/stores/supplyStore';
import { commandTargetActions } from '@/stores/commandTargetStore';
import { useCanCommand, useCurrentUser } from '@/stores/authStore';
import {
  subscribeToDrone,
  useDroneIdentity,
  useDroneScalars,
  useDroneSelection,
  usePrimarySelectedId,
  useSelectedDroneIds,
} from '@/stores/droneStore';
import { useMapStore } from '@/stores/mapStore';
import { missionActions, useDisplayMission } from '@/stores/missionStore';
import { useWorkspaceStore } from '@/stores/workspaceStore';
import type { CommandResult, DroneId } from '@/types/domain';

/** Radius a right-click "orbit here" uses, in metres. */
const ORBIT_RADIUS_M = 120;
const CONFIRM_WINDOW_MS = 4000;
/** How often Command re-reads the mission records the server keeps current. */
const MISSION_POLL_MS = 10_000;
const FOLLOW_INTERVAL_MS = 2000;

/** Set once the operator has flown into an AOR, so returning to Command from
 *  another screen lands on the tactical view rather than replaying the intro. */
let hasLanded = false;

const COPTER_MODES = ['STABILIZE', 'ALT_HOLD', 'LOITER', 'POSHOLD', 'GUIDED', 'AUTO', 'RTL', 'SMART_RTL', 'LAND', 'BRAKE'];
const PLANE_MODES = ['MANUAL', 'FLY_BY_WIRE_A', 'CRUISE', 'LOITER', 'GUIDED', 'AUTO', 'RTL', 'QLOITER', 'QLAND'];
const ROVER_MODES = ['MANUAL', 'HOLD', 'LOITER', 'GUIDED', 'AUTO', 'RTL', 'SMART_RTL'];
const PLANE_TYPES = new Set([1, 19, 20, 21, 22, 23, 24, 25]);
const ROVER_TYPES = new Set([10, 11]);

function modesFor(mavType: number | undefined): string[] {
  if (mavType === undefined) return COPTER_MODES;
  if (PLANE_TYPES.has(mavType)) return PLANE_MODES;
  if (ROVER_TYPES.has(mavType)) return ROVER_MODES;
  return COPTER_MODES;
}

// -------------------------------------------------------------- fly transitions

/** The concrete MapLibre map, as the controller hands it out. */
type RawMap = NonNullable<ReturnType<MapController['raw']>>;

function flyToAor(map: RawMap, aor: Aor): void {
  const dest = { center: aor.center, zoom: aor.zoom, pitch: aor.pitch, bearing: 0 };
  map.flyTo({ ...dest, duration: 3500, curve: 1.42, essential: true });
  // A capable GPU animates the fly-in; a weak one starves the render loop and
  // the camera never arrives. Guarantee arrival by snapping to the destination
  // if the animation has not progressed.
  window.setTimeout(() => {
    if (Math.abs(map.getZoom() - aor.zoom) > 0.5) map.jumpTo(dest);
  }, 3800);
}

function flyToOrbit(map: RawMap): void {
  // jumpTo, not flyTo: animating out to the full-globe zoom starves the render
  // loop on modest GPUs and the camera never arrives. An instant return to the
  // globe is reliable everywhere; the fly-in stays animated where it can.
  map.jumpTo({ center: ORBIT.center, zoom: ORBIT.zoom, pitch: 0, bearing: 0 });
}

// -------------------------------------------------------------- AOR points

const AOR_STYLE_ID = 'px-aor-styles';
function ensureAorStyles(): void {
  if (document.getElementById(AOR_STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = AOR_STYLE_ID;
  style.textContent = `
.px-aor{display:inline-flex;align-items:center;gap:7px;padding:6px 12px;border-radius:99px;
  background:rgba(18,18,20,0.72);border:1px solid rgba(255,255,255,0.18);backdrop-filter:blur(10px);
  color:#f4f4f5;font:600 10px 'Geist Mono',ui-monospace,monospace;letter-spacing:0.06em;white-space:nowrap;
  cursor:pointer;box-shadow:0 6px 18px rgba(0,0,0,0.5);transition:background 120ms}
.px-aor:hover{background:rgba(32,32,36,0.88);border-color:rgba(255,255,255,0.3)}
.px-aor-dot{width:7px;height:7px;border-radius:99px;background:oklch(0.70 0.13 245);box-shadow:0 0 8px oklch(0.70 0.13 245)}`;
  document.head.appendChild(style);
}

/** AOR pills on the globe, shown at strategic/regional zoom, click to fly in. */
function useAorLayer(): void {
  const controller = useMapController();
  const lod = useMapStore((s) => s.lod);
  const entered = useWorkspaceStore((s) => s.entered);
  const flyTo = useMapStore((s) => s.flyTo);
  const counts = useAorCounts();
  const elsRef = useRef<Array<{ id: string; el: HTMLElement }>>([]);

  useEffect(
    () =>
      controller.ready((map: MapLike) => {
        ensureAorStyles();
        const markers = AORS.map((a) => {
          const el = document.createElement('button');
          el.type = 'button';
          el.className = 'px-aor';
          // Markers can be created after the visibility effect last ran (the
          // style loads late, or the map is rebuilt mid-session), so the
          // initial state comes from the stores, not from a default.
          if (
            useWorkspaceStore.getState().entered ||
            useMapStore.getState().lod === 'tactical'
          ) {
            el.style.display = 'none';
          }
          el.innerHTML = `<span class="px-aor-dot"></span><span class="px-aor-label">AOR ${a.id} · 0 TRK</span>`;
          el.addEventListener('click', (event) => {
            event.stopPropagation();
            flyTo(a.id);
          });
          return { marker: createMapMarker(map, el, a.center), el, id: a.id };
        });
        elsRef.current = markers.map((m) => ({ id: m.id, el: m.el }));
        return () => {
          for (const m of markers) m.marker.remove();
          elsRef.current = [];
        };
      }),
    [controller, flyTo],
  );

  // Clicking anywhere inside an AOR on the zoomed-out view enters it. The pills
  // are small targets; the box is what an operator actually points at. Only on
  // the strategic view and only before entering, so this can never steal the
  // click from GOTO or from placing anything inside an AOR.
  useEffect(
    () =>
      controller.onClick((point) => {
        if (useWorkspaceStore.getState().entered) return;
        if (useMapStore.getState().lod === 'tactical') return;
        const map = controller.raw();
        if (!map) return;
        const hit = map
          .queryRenderedFeatures(map.project([point.lon, point.lat]), { layers: ['aor-fill'] })
          .find((feature) => typeof feature.properties?.aorId === 'string');
        const aorId = hit?.properties?.aorId;
        if (typeof aorId === 'string') flyTo(aorId);
      }),
    [controller, flyTo],
  );

  // Live per-AOR track counts, without rebuilding the markers.
  useEffect(() => {
    for (const { id, el } of elsRef.current) {
      const label = el.querySelector('.px-aor-label');
      if (label) label.textContent = `AOR ${id} · ${counts[id] ?? 0} TRK`;
    }
  }, [counts]);

  // The pills are the ENTRY affordance on the strategic globe. Once inside the
  // command centre they are noise pinned to the AOR's coordinates — AOR
  // switching happens in the header — so `entered` hides them outright, not
  // just at tactical zoom. RETURN TO ORBIT clears `entered` and they return.
  useEffect(() => {
    const display = entered || lod === 'tactical' ? 'none' : '';
    for (const { el } of elsRef.current) el.style.display = display;
  }, [lod, entered]);
}

// -------------------------------------------------------------- strategic HUD

function StrategicOverlay(): ReactNode {
  const aorId = useWorkspaceStore((s) => s.aor);
  const flyTo = useMapStore((s) => s.flyTo);
  const lod = useMapStore((s) => s.lod);
  const aor = aorById(aorId);
  const isMobile = useIsMobile();

  // ADS-B sits under UNKNOWN, not NEUTRAL. This legend said NEUTRAL for a while
  // and it was telling the operator to look for cooperative traffic in green,
  // when app/domain/adsb.py maps every ADS-B contact to UNKNOWN — a civil
  // aircraft that chose to broadcast is not a vouched-for neutral, and no source
  // that cannot assert FRIEND should be able to imply it either. A legend that
  // names the wrong colour is worse than no legend, because it is trusted.
  const legend: Array<[string, string]> = [
    ['FRIEND — own UAS', 'var(--friend)'],
    ['HOSTILE', 'var(--hostile)'],
    ['NEUTRAL — vouched for', 'var(--neutral)'],
    ['UNKNOWN — ADS-B, external', 'var(--unknown)'],
  ];

  // A phone gets a layout of its own. The desktop overlays are pinned to fixed
  // corners at desktop sizes, so at 390px the status pill and the FLY button
  // collide into each other (the AOR name clips to "AOR JA...") and the
  // affiliation panel covers most of the globe. On mobile: status centered
  // under the top bar, affiliation shrunk to diamonds so it stops eating the
  // globe, and FLY INTO AOR as one full-width thumb target above the tab bar.
  if (isMobile) {
    return (
      <div className="pointer-events-none absolute inset-0 z-20">
        <div
          className="pointer-events-auto absolute left-1/2 -translate-x-1/2 whitespace-nowrap"
          style={{ top: 'calc(var(--safe-t) + 56px)' }}
        >
          <div className="glass-pill flex items-center gap-2 rounded-[99px] px-3.5 py-2">
            <span className="size-[6px] rounded-full px-pulse" style={{ background: 'var(--ok)' }} />
            <span className="label-mono text-[9.5px]">
              {lod === 'strategic' ? 'STRATEGIC' : 'REGIONAL'}
            </span>
            <span className="label-mono text-[9.5px] text-[color:var(--muted-foreground-3)]">
              AOR {aor.id}
            </span>
          </div>
        </div>

        <div
          className="pointer-events-auto absolute left-[14px]"
          style={{ bottom: 'calc(var(--safe-b) + var(--mobile-nav-h, 60px) + 64px)' }}
        >
          <div className="glass flex items-center gap-2.5 rounded-[99px] px-3 py-2">
            {legend.map(([label, color]) => (
              <span
                key={label}
                aria-label={label}
                className="size-2.5 rotate-45 rounded-[2px] border-2"
                style={{ borderColor: color }}
              />
            ))}
          </div>
        </div>

        <div
          className="pointer-events-auto absolute inset-x-[14px]"
          style={{ bottom: 'calc(var(--safe-b) + var(--mobile-nav-h, 60px) + 10px)' }}
        >
          <button
            type="button"
            onClick={() => flyTo(aor.id)}
            className="w-full rounded-[99px] px-5 py-3.5 label-mono text-[12px] font-semibold"
            style={{ letterSpacing: '0.12em', background: 'oklch(1 0 0 / 0.92)', color: '#0c0c0d' }}
          >
            ▸ FLY INTO {aor.id}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="pointer-events-none absolute inset-0 z-20">
      <div className="pointer-events-auto absolute left-[14px] top-[86px]">
        <div className="glass-pill flex items-center gap-2.5 rounded-[99px] px-4 py-2.5">
          <span className="size-[7px] rounded-full px-pulse" style={{ background: 'var(--ok)' }} />
          <span className="label-mono text-[10px]">{lod === 'strategic' ? 'STRATEGIC' : 'REGIONAL'}</span>
          <span className="label-mono text-[10px] text-[color:var(--muted-foreground-3)]">
            AOR {aor.id} · {aor.sub}
          </span>
        </div>
      </div>

      <div className="pointer-events-auto absolute right-[14px] top-[86px]">
        <button
          type="button"
          onClick={() => flyTo(aor.id)}
          className="glass-pill rounded-[99px] px-5 py-2.5 label-mono text-[10px]"
          style={{ letterSpacing: '0.1em' }}
        >
          ▸ FLY INTO AOR
        </button>
      </div>

      <div className="pointer-events-auto absolute bottom-[14px] right-[14px]">
        <div className="glass flex flex-col gap-1.5 rounded-[16px] p-3.5">
          <span className="label-mono mb-1 text-[8.5px] text-[color:var(--muted-foreground-2)]">
            TRACK AFFILIATION
          </span>
          {legend.map(([label, color]) => (
            <span key={label} className="flex items-center gap-2 text-[11px]">
              <span
                className="size-2.5 rotate-45 rounded-[2px] border-2"
                style={{ borderColor: color }}
              />
              {label}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

// -------------------------------------------------------------- tactical dock

type PillVariant = 'primary' | 'secondary' | 'danger' | 'armed';

function Pill({
  variant = 'secondary',
  className,
  children,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: PillVariant; children: ReactNode }) {
  const styles: Record<PillVariant, string> = {
    primary: 'bg-[oklch(1_0_0/0.9)] text-[#0c0c0d] font-semibold',
    secondary: 'bg-[oklch(1_0_0/0.06)] border border-[color:var(--border)] text-foreground hover:bg-[oklch(1_0_0/0.1)]',
    danger:
      'text-[color:var(--danger-foreground)] font-semibold bg-[color-mix(in_oklch,var(--danger)_18%,transparent)] border border-[color-mix(in_oklch,var(--danger)_55%,transparent)]',
    armed: 'bg-[color:var(--armed)] text-[#0c0c0d] font-semibold',
  };
  return (
    <button
      type="button"
      className={cn(
        'hit-44 inline-flex items-center gap-1.5 rounded-[99px] px-[14px] py-[7px] text-[11px] label-mono transition-colors disabled:opacity-35',
        styles[variant],
        className,
      )}
      style={{ letterSpacing: '0.06em' }}
      {...rest}
    >
      {children}
    </button>
  );
}

interface CommandOutcome {
  droneId: DroneId;
  ok: boolean;
  message: string;
}

interface Runner {
  busyKey: string | null;
  outcomes: CommandOutcome[];
  label: string | null;
  run: (key: string, label: string, ids: DroneId[], command: (droneId: DroneId) => Promise<ApiResult<CommandResult>>) => Promise<void>;
  clear: () => void;
}

function useCommandRunner(): Runner {
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [outcomes, setOutcomes] = useState<CommandOutcome[]>([]);
  const [label, setLabel] = useState<string | null>(null);
  const inFlight = useRef(false);

  const run = useCallback<Runner['run']>(async (key, commandLabel, ids, command) => {
    if (inFlight.current || ids.length === 0) return;
    inFlight.current = true;
    setBusyKey(key);
    setLabel(commandLabel);
    setOutcomes([]);
    try {
      const results = await fanOut(ids, command);
      setOutcomes(
        results.map(({ droneId, result }) => ({
          droneId,
          ok: result.ok,
          message: result.ok ? result.data.message : result.error.message,
        })),
      );
    } finally {
      inFlight.current = false;
      setBusyKey(null);
    }
  }, []);

  const clear = useCallback(() => {
    setOutcomes([]);
    setLabel(null);
  }, []);

  return { busyKey, outcomes, label, run, clear };
}

/**
 * Retire the on-map target when the order that owns it is superseded.
 *
 * The marker means "this aircraft is going here now". An RTL, a landing, a mode
 * change or a mission start all take the aircraft somewhere the point command
 * did not ask for, so leaving the marker up turns it into a claim about the
 * flight that is no longer true. Disarming ends the sortie, and with it the
 * sensor is no longer looking anywhere either.
 */
function retireTargets(key: string, droneId: DroneId): void {
  if (key === 'disarm') {
    commandTargetActions.forget(droneId);
    return;
  }
  if (key === 'land' || key === 'rtl' || key === 'mission' || key.startsWith('mode-')) {
    commandTargetActions.clearNav(droneId);
  }
}

function Readout({ label, value, tone }: { label: string; value: string; tone?: string }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="label-mono text-[8px] text-[color:var(--muted-foreground-2)]">{label}</span>
      <span className="tnum text-[13px]" style={tone ? { color: tone } : undefined}>
        {value}
      </span>
    </div>
  );
}

function TelemetryStrip({ droneId }: { droneId: DroneId }) {
  const scalars = useDroneScalars(droneId, [
    'altRelative',
    'groundSpeed',
    'verticalSpeed',
    'heading',
    'batteryPercent',
    'batteryVoltage',
    'gpsSatellites',
    'currentWaypoint',
  ]);
  const batt = scalars.batteryPercent;
  const battTone = batt === undefined ? undefined : batt <= 15 ? 'var(--danger-foreground)' : batt <= 30 ? 'var(--caution)' : undefined;
  return (
    <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
      <Readout label="ALT" value={scalars.altRelative === undefined ? '---' : `${scalars.altRelative.toFixed(1)} m`} />
      <Readout label="GROUND" value={scalars.groundSpeed === undefined ? '---' : `${scalars.groundSpeed.toFixed(1)} m/s`} />
      <Readout label="CLIMB" value={scalars.verticalSpeed === undefined ? '---' : `${scalars.verticalSpeed >= 0 ? '+' : ''}${scalars.verticalSpeed.toFixed(1)} m/s`} />
      <Readout label="HDG" value={scalars.heading === undefined ? '---' : `${scalars.heading.toFixed(0)}°`} />
      <Readout
        label="BATT"
        tone={battTone}
        value={batt === undefined ? '---' : `${batt.toFixed(0)}%${scalars.batteryVoltage === undefined ? '' : ` ${scalars.batteryVoltage.toFixed(1)}V`}`}
      />
      <Readout label="SATS" value={`${scalars.gpsSatellites ?? '--'}`} />
      <Readout label="WP" value={`${scalars.currentWaypoint ?? '--'}`} />
    </div>
  );
}

function OutcomeList({ runner }: { runner: Runner }) {
  if (!runner.outcomes.length) return null;
  const failed = runner.outcomes.filter((outcome) => !outcome.ok).length;
  return (
    <div className="glass-2 rounded-[14px]">
      <div className="flex items-center gap-2 px-3 py-2">
        <span className="label-mono text-[9px]">{runner.label}</span>
        <span
          className="rounded-[99px] px-2 py-0.5 label-mono text-[8.5px]"
          style={{
            color: failed ? 'var(--danger-foreground)' : 'var(--ok)',
            background: failed ? 'color-mix(in oklch, var(--danger) 18%, transparent)' : 'color-mix(in oklch, var(--ok) 16%, transparent)',
          }}
        >
          {failed ? `${failed} OF ${runner.outcomes.length} REFUSED` : `${runner.outcomes.length} ACCEPTED`}
        </span>
        <button type="button" aria-label="Dismiss command results" className="ml-auto text-[color:var(--muted-foreground)] hover:text-foreground" onClick={runner.clear}>
          <X className="size-3.5" />
        </button>
      </div>
      <ul className="scroll-thin max-h-28 overflow-y-auto border-t border-[color:var(--border-nested)]">
        {runner.outcomes.map((outcome) => (
          <li key={outcome.droneId} className="flex items-start gap-2 px-3 py-1.5 text-[11px]">
            {outcome.ok ? (
              <Check className="mt-0.5 size-3.5 shrink-0" style={{ color: 'var(--ok)' }} />
            ) : (
              <Ban className="mt-0.5 size-3.5 shrink-0" style={{ color: 'var(--danger-foreground)' }} />
            )}
            <span className="tnum w-24 shrink-0 truncate">{outcome.droneId}</span>
            <span className="min-w-0 flex-1" style={!outcome.ok ? { color: 'var(--danger-foreground)' } : undefined}>
              {outcome.message}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

interface DockProps {
  gotoArmed: boolean;
  setGotoArmed: (value: boolean) => void;
  follow: boolean;
  setFollow: (value: boolean) => void;
  runner: Runner;
}

function CommandDock({ gotoArmed, setGotoArmed, follow, setFollow, runner }: DockProps) {
  const selectedIds = useSelectedDroneIds();
  const primaryId = usePrimarySelectedId();
  const identity = useDroneIdentity(primaryId ?? '');
  const scalars = useDroneScalars(primaryId ?? '', ['armed', 'altRelative', 'mavType']);
  const mission = useDisplayMission(primaryId);
  const canCommand = useCanCommand();
  const { clearSelection } = useDroneSelection();

  const userId = useCurrentUser()?.userId ?? null;
  const [takeoffAlt, setTakeoffAlt] = useState(() => loadTakeoffAltitude(userId));
  const [confirmDisarm, setConfirmDisarm] = useState(false);
  const [confirmForceArm, setConfirmForceArm] = useState(false);

  // The field is filled before the session has said who is signed in, and a
  // different operator can sign in on the same tab.
  useEffect(() => {
    setTakeoffAlt(loadTakeoffAltitude(userId));
  }, [userId]);

  useEffect(() => {
    if (!confirmDisarm) return undefined;
    const timer = setTimeout(() => setConfirmDisarm(false), CONFIRM_WINDOW_MS);
    return () => clearTimeout(timer);
  }, [confirmDisarm]);

  useEffect(() => {
    if (!confirmForceArm) return undefined;
    const timer = setTimeout(() => setConfirmForceArm(false), CONFIRM_WINDOW_MS);
    return () => clearTimeout(timer);
  }, [confirmForceArm]);

  const modes = useMemo(() => modesFor(scalars.mavType), [scalars.mavType]);

  if (!primaryId || !identity) {
    return (
      <div className="glass rounded-[18px] px-4 py-3 text-[12px] text-[color:var(--muted-foreground)]">
        Select an aircraft in the fleet list, or click its marker, to command it.
      </div>
    );
  }

  const airborne = (scalars.altRelative ?? 0) > 1;
  const armed = scalars.armed === true;
  const disarmNeedsConfirm = armed && airborne;
  const busy = runner.busyKey !== null;
  const targets = selectedIds.length ? selectedIds : [primaryId];

  const command = (key: string, label: string, run: (droneId: DroneId) => Promise<ApiResult<CommandResult>>) => {
    void runner.run(key, label, targets, async (droneId) => {
      const result = await run(droneId);
      if (result.ok) retireTargets(key, droneId);
      return result;
    });
  };

  const takeoffMetres = Number.parseFloat(takeoffAlt);
  const takeoffValid = isValidTakeoffAltitude(takeoffMetres);
  // A rover or boat refuses takeoff on the server; the button says so first.
  const surface = isSurfaceVehicle(scalars.mavType);

  const chip = (text: string, color: string) => (
    <span className="rounded-[99px] px-2 py-0.5 label-mono text-[8.5px]" style={{ color, background: `color-mix(in oklch, ${color} 16%, transparent)` }}>
      {text}
    </span>
  );

  return (
    <div className="glass flex w-[min(var(--dock-max),calc(100vw-var(--rail-l-w)-var(--rail-r-w)-80px))] min-w-[420px] flex-col gap-3 rounded-[18px] p-3 tablet:w-[min(680px,calc(100vw-28px-var(--dock-inset,0px)))] tablet:min-w-0 phone:w-full phone:min-w-0">
      <div className="flex items-center gap-2">
        <span className="tnum text-[15px] font-semibold">{identity.callsign}</span>
        {armed ? chip('ARMED', 'var(--armed)') : chip('DISARMED', 'var(--muted-foreground-3)')}
        {identity.state !== 'connected' ? chip(identity.state.toUpperCase(), 'var(--danger-foreground)') : null}
        {mission.waypoints.length
          ? chip(mission.onVehicle ? `${mission.waypoints.length} WP ON AIRCRAFT` : `${mission.waypoints.length} WP · NOT UPLOADED`, mission.onVehicle ? 'var(--ok)' : 'var(--caution)')
          : null}
        {selectedIds.length > 1 ? chip(`COMMANDING ${selectedIds.length}`, 'var(--friend)') : null}
        <button type="button" onClick={clearSelection} disabled={busy} className="ml-auto label-mono text-[9px] text-[color:var(--muted-foreground-3)] hover:text-foreground disabled:opacity-40">
          DESELECT
        </button>
      </div>

      <TelemetryStrip droneId={primaryId} />
      <div className="h-px bg-[color:var(--border-nested)]" />

      {!canCommand ? (
        <p className="text-[11px] text-[color:var(--muted-foreground)]">
          Your role can watch this aircraft but not fly it. Ask an administrator for operator access on this team.
        </p>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <Pill variant="armed" disabled={busy || armed} onClick={() => command('arm', 'ARM', (id) => droneCommands.arm(id))}>
              ARM
            </Pill>
            {/* Past the pre-arm checks and the preflight gate. The operator who
                needs this has usually just read why ARM was refused, so it sits
                beside it, and it asks once more because it skips the checks
                that catch a bad compass or a flat pack. */}
            <Pill
              variant={confirmForceArm ? 'danger' : 'secondary'}
              disabled={busy || armed}
              title="Arm without the autopilot's pre-arm checks or the preflight gate"
              onClick={() => {
                if (!confirmForceArm) {
                  setConfirmForceArm(true);
                  return;
                }
                setConfirmForceArm(false);
                command('arm', 'FORCE ARM', (id) => droneCommands.forceArm(id));
              }}
            >
              {confirmForceArm ? 'CONFIRM FORCE ARM' : 'FORCE ARM'}
            </Pill>
            <Pill
              variant={confirmDisarm ? 'danger' : 'secondary'}
              disabled={busy || !armed}
              onClick={() => {
                if (disarmNeedsConfirm && !confirmDisarm) {
                  setConfirmDisarm(true);
                  return;
                }
                setConfirmDisarm(false);
                command('disarm', 'DISARM', (id) => droneCommands.disarm(id));
              }}
            >
              {confirmDisarm ? 'CONFIRM DISARM IN FLIGHT' : 'DISARM'}
            </Pill>
            <span className="mx-0.5 h-6 w-px bg-[color:var(--border-nested)]" />
            <div className="flex items-center gap-1.5">
              <Pill
                disabled={busy || !armed || !takeoffValid || surface}
                title={surface ? 'Rovers and boats do not take off' : undefined}
                onClick={() => command('takeoff', `TAKEOFF TO ${takeoffMetres} M`, (id) => droneCommands.takeoff(id, takeoffMetres))}
              >
                TAKEOFF
              </Pill>
              <input
                value={takeoffAlt}
                onChange={(event) => {
                  setTakeoffAlt(event.target.value);
                  saveTakeoffAltitude(userId, event.target.value);
                }}
                inputMode="decimal"
                aria-label="Takeoff altitude in metres"
                className={cn('well tnum h-8 w-14 rounded-[99px] text-center text-[12px] outline-none pointer-coarse:h-11 pointer-coarse:w-16', !takeoffValid && 'border border-[color:var(--danger)]')}
              />
              <span className="label-mono text-[8.5px] text-[color:var(--muted-foreground-3)]">m AGL</span>
            </div>
            <Pill disabled={busy} onClick={() => command('land', 'LAND', (id) => droneCommands.land(id))}>
              LAND
            </Pill>
            <Pill disabled={busy} onClick={() => command('rtl', 'RETURN TO LAUNCH', (id) => droneCommands.returnToLaunch(id))}>
              RTL
            </Pill>
            <Pill disabled={busy || !mission.onVehicle} title={mission.onVehicle ? 'Switch to AUTO and start the loaded mission' : 'No mission uploaded to this aircraft'} onClick={() => command('mission', 'START MISSION', (id) => startMission(id))}>
              START MSN
            </Pill>
            {/* Hold and continue, not stop and restart: a paused survey resumes
                on the leg it reached. Both are refused with a plain-language
                reason when there is no mission running to hold. */}
            <Pill disabled={busy || !mission.onVehicle} title="Hold at the current position, keeping the mission and its progress" onClick={() => command('mission', 'PAUSE MISSION', (id) => pauseMission(id))}>
              PAUSE
            </Pill>
            <Pill disabled={busy || !mission.onVehicle} title="Continue the held mission from the leg it stopped on" onClick={() => command('mission', 'RESUME MISSION', (id) => resumeMission(id))}>
              RESUME
            </Pill>
            {selectedIds.length > 1 ? (
              <FormationPills
                leaderId={primaryId}
                followerIds={selectedIds.filter((id) => id !== primaryId)}
                disabled={busy}
              />
            ) : null}
            <Pill variant={gotoArmed ? 'primary' : 'secondary'} disabled={busy} onClick={() => setGotoArmed(!gotoArmed)}>
              {gotoArmed ? 'CLICK MAP…' : 'GOTO'}
            </Pill>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <span className="label-mono text-[8.5px] text-[color:var(--muted-foreground-3)]">MODE</span>
            <select
              disabled={busy}
              value=""
              onChange={(event) => {
                const mode = event.target.value;
                if (!mode) return;
                command(`mode-${mode}`, `MODE ${mode}`, (id) => droneCommands.setMode(id, mode));
                event.target.value = '';
              }}
              className="well h-8 rounded-[99px] px-3 text-[11px] outline-none label-mono pointer-coarse:h-11"
            >
              <option value="">SELECT</option>
              {modes.map((mode) => (
                <option key={mode} value={mode}>
                  {mode}
                </option>
              ))}
            </select>
            <label className="ml-auto flex items-center gap-2 label-mono text-[8.5px] text-[color:var(--muted-foreground-3)]">
              FOLLOW
              <button
                type="button"
                role="switch"
                aria-checked={follow}
                onClick={() => setFollow(!follow)}
                className="hit-44 relative h-5 w-9 rounded-[99px] transition-colors"
                style={{ background: follow ? 'var(--friend)' : 'oklch(1 0 0 / 0.15)' }}
              >
                <span className="absolute top-0.5 size-4 rounded-full bg-white transition-all" style={{ left: follow ? 18 : 2 }} />
              </button>
            </label>
          </div>
        </>
      )}

      <OutcomeList runner={runner} />
    </div>
  );
}

function useFollowSelected(enabled: boolean): void {
  const controller = useMapController();
  const primaryId = usePrimarySelectedId();
  useEffect(() => {
    if (!enabled || !primaryId) return undefined;
    let lastMove = 0;
    return subscribeToDrone(primaryId, (drone) => {
      const map = controller.raw();
      if (!map || !drone) return;
      const { lat, lon } = drone.scalars;
      if (lat === undefined || lon === undefined) return;
      if (lat === 0 && lon === 0) return;
      const now = Date.now();
      if (now - lastMove < FOLLOW_INTERVAL_MS) return;
      const point = map.project([lon, lat]);
      const canvas = map.getCanvas();
      const insideX = Math.abs(point.x - canvas.clientWidth / 2) < canvas.clientWidth * 0.3;
      const insideY = Math.abs(point.y - canvas.clientHeight / 2) < canvas.clientHeight * 0.3;
      if (insideX && insideY) return;
      lastMove = now;
      map.easeTo({ center: [lon, lat], duration: 600 });
    });
  }, [controller, enabled, primaryId]);
}

/**
 * Form the selection up on the primary aircraft.
 *
 * The primary selected drone leads and the rest take stations around it. One
 * reposition each rather than continuous station-keeping, so the label says
 * FORM UP rather than implying the shape will be held.
 */
function FormationPills({
  leaderId,
  followerIds,
  disabled,
}: {
  leaderId: DroneId | null;
  followerIds: DroneId[];
  disabled: boolean;
}) {
  const [shape, setShape] = useState<FormationKind>('v');
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  const go = async () => {
    if (!leaderId || followerIds.length === 0) return;
    setBusy(true);
    const result = await formUp({
      leaderId,
      followerIds,
      formation: shape,
      spacingM: 120,
    });
    setBusy(false);
    if (!result.ok) {
      setNote(result.error.message);
      return;
    }
    // One line per aircraft, not one aggregate: "formed 4" is a lie when two
    // refused, and a formation with holes in it is the thing to look at.
    const refused = result.data.outcomes.filter((o) => !o.ok);
    setNote(
      refused.length === 0
        ? `Formed ${FORMATION_LABEL[shape]} on ${leaderId}`
        : `${refused.length} refused: ${refused.map((o) => o.droneId).join(', ')}`,
    );
  };

  return (
    <>
      <select
        value={shape}
        onChange={(event) => setShape(event.target.value as FormationKind)}
        aria-label="Formation shape"
        className="well h-[26px] rounded-[99px] px-2 font-mono text-[10px] outline-none pointer-coarse:h-11"
      >
        {(Object.keys(FORMATION_LABEL) as FormationKind[]).map((option) => (
          <option key={option} value={option}>
            {FORMATION_LABEL[option]}
          </option>
        ))}
      </select>
      <Pill
        disabled={disabled || busy || !leaderId}
        title={`Send the other ${followerIds.length} to stations around ${leaderId ?? 'the leader'}`}
        onClick={() => void go()}
      >
        {busy ? 'FORMING…' : `FORM UP ×${followerIds.length}`}
      </Pill>
      {note ? <span className="font-mono text-[9px] text-white/55">{note}</span> : null}
    </>
  );
}

/** The tactical COP: right rail, command dock, GOTO, and a return-to-orbit control. */
function TacticalOverlay(): ReactNode {
  const primaryId = usePrimarySelectedId();
  const selectedIds = useSelectedDroneIds();
  const controller = useMapController();
  const chrome = useWorkspaceStore((state) => state.chrome);
  const flyTo = useMapStore((state) => state.flyTo);
  const canCommand = useCanCommand();
  const [gotoArmed, setGotoArmed] = useState(false);
  const [follow, setFollow] = useState(false);
  const [pendingGoto, setPendingGoto] = useState<{ lat: number; lon: number } | null>(null);
  const [pendingOrbit, setPendingOrbit] = useState<{ lat: number; lon: number } | null>(null);
  const runner = useCommandRunner();
  const tier = useViewport();
  const isMobile = tier === 'phone';
  const isTablet = tier === 'tablet';
  const railDrawer = useWorkspaceStore((state) => state.railDrawer);
  const toggleRailDrawer = useWorkspaceStore((state) => state.toggleRailDrawer);
  const [sheetTab, setSheetTab] = useState<'fleet' | 'command' | 'layers'>('fleet');
  const [sheetHeight, setSheetHeight] = useState<SheetHeight>('peek');

  useFollowSelected(follow);

  useEffect(() => {
    if (!gotoArmed) return undefined;
    return controller.onClick((point) => setPendingGoto({ lat: point.lat, lon: point.lon }));
  }, [controller, gotoArmed]);

  const altitude = useDroneScalars(primaryId ?? '', ['altRelative']).altRelative;
  const targets = selectedIds.length ? selectedIds : primaryId ? [primaryId] : [];

  // Right-click on the map: real, point-targeted commands against the selection,
  // plus a couple of map utilities. Commands that move the flight path (GOTO,
  // orbit) go through a confirm card; sensor/mode actions run immediately.
  const buildMenu: MenuBuilder = (point) => {
    const coord = `${point.lat.toFixed(5)}, ${point.lon.toFixed(5)}`;
    const copyItem: MenuItem = {
      label: 'Copy coordinates',
      sub: coord,
      onSelect: () =>
        void navigator.clipboard?.writeText(`${point.lat.toFixed(6)}, ${point.lon.toFixed(6)}`),
    };
    const centerItem: MenuItem = {
      label: 'Center map here',
      sub: coord,
      onSelect: () => controller.flyTo(point.lat, point.lon),
    };
    if (!primaryId || !canCommand) {
      return [
        {
          label: !primaryId ? 'Select an aircraft to command' : 'Operator access required',
          disabled: true,
        },
        { label: '', separator: true },
        centerItem,
        copyItem,
      ];
    }
    const many = targets.length > 1;
    return [
      {
        label: many ? `Fly ${targets.length} aircraft here` : `Fly ${primaryId} here`,
        sub: `GOTO · ${coord}`,
        onSelect: () => setPendingGoto({ lat: point.lat, lon: point.lon }),
      },
      {
        label: 'Orbit here',
        sub: `CIRCLE R${ORBIT_RADIUS_M} m · ${coord}`,
        onSelect: () => setPendingOrbit({ lat: point.lat, lon: point.lon }),
      },
      {
        label: 'Look here (ROI)',
        sub: `point sensor · ${coord}`,
        onSelect: () =>
          void runner.run('roi', 'SET ROI', targets, async (id) => {
            const result = await droneCommands.setRoi(id, point.lat, point.lon, 0);
            // Only an accepted command marks the map. A refused ROI that still
            // drew a marker would be a claim about a sensor that never moved.
            if (result.ok) commandTargetActions.recordRoi(id, point.lat, point.lon);
            return result;
          }),
      },
      {
        label: 'Loiter (hold position)',
        sub: many ? `${targets.length} aircraft` : primaryId,
        onSelect: () =>
          void runner.run('mode-LOITER', 'MODE LOITER', targets, async (id) => {
            const result = await droneCommands.setMode(id, 'LOITER');
            if (result.ok) retireTargets('mode-LOITER', id);
            return result;
          }),
      },
      {
        label: 'Return to launch',
        sub: many ? `${targets.length} aircraft` : primaryId,
        danger: true,
        onSelect: () =>
          void runner.run('rtl', 'RETURN TO LAUNCH', targets, async (id) => {
            const result = await droneCommands.returnToLaunch(id);
            if (result.ok) retireTargets('rtl', id);
            return result;
          }),
      },
      { label: '', separator: true },
      centerItem,
      copyItem,
    ];
  };

  if (!chrome) return null;

  const gotoCard =
    pendingGoto && primaryId ? (
      <div className="glass pointer-events-auto rounded-[16px] px-4 py-3" style={{ borderColor: 'color-mix(in oklch, var(--friend) 55%, transparent)' }}>
        <p className="text-[12px]">
          Send {targets.length > 1 ? `${targets.length} aircraft` : primaryId} to{' '}
          <span className="tnum">
            {pendingGoto.lat.toFixed(6)}, {pendingGoto.lon.toFixed(6)}
          </span>{' '}
          at {targets.length > 1 ? 'their' : 'its'} current height
          {targets.length > 1 ? '' : ` of ${(altitude ?? 0).toFixed(0)} m`}?
        </p>
        <div className="mt-2 flex gap-2">
          <Pill
            variant="primary"
            disabled={runner.busyKey !== null}
            onClick={() => {
              const target = pendingGoto;
              const alt = altitude ?? 0;
              setPendingGoto(null);
              setGotoArmed(false);
              void runner.run('goto', 'GO TO POINT', targets, async (id) => {
                const result = await droneCommands.goto(id, target.lat, target.lon, alt);
                if (result.ok) commandTargetActions.recordGoto(id, target.lat, target.lon, alt);
                return result;
              });
            }}
          >
            SEND GOTO
          </Pill>
          <Pill onClick={() => setPendingGoto(null)}>CANCEL</Pill>
        </div>
      </div>
    ) : null;

  const orbitCard =
    pendingOrbit && primaryId ? (
      <div className="glass pointer-events-auto rounded-[16px] px-4 py-3" style={{ borderColor: 'color-mix(in oklch, var(--friend) 55%, transparent)' }}>
        <p className="text-[12px]">
          Orbit {primaryId} around{' '}
          <span className="tnum">
            {pendingOrbit.lat.toFixed(6)}, {pendingOrbit.lon.toFixed(6)}
          </span>{' '}
          at radius <span className="tnum">{ORBIT_RADIUS_M} m</span> and its current height of{' '}
          <span className="tnum">{(altitude ?? 0).toFixed(0)} m</span>?
        </p>
        <div className="mt-2 flex gap-2">
          <Pill
            variant="primary"
            disabled={runner.busyKey !== null}
            onClick={() => {
              const target = pendingOrbit;
              const alt = altitude ?? 0;
              setPendingOrbit(null);
              void runner.run('orbit', 'ORBIT POINT', [primaryId], async (id) => {
                const result = await droneCommands.orbit(
                  id,
                  target.lat,
                  target.lon,
                  ORBIT_RADIUS_M,
                  alt,
                );
                if (result.ok) {
                  commandTargetActions.recordOrbit(id, target.lat, target.lon, ORBIT_RADIUS_M, alt);
                }
                return result;
              });
            }}
          >
            SEND ORBIT
          </Pill>
          <Pill onClick={() => setPendingOrbit(null)}>CANCEL</Pill>
        </div>
      </div>
    ) : null;

  if (isMobile) {
    const tabs: Array<{ id: 'fleet' | 'command' | 'layers'; label: string }> = [
      { id: 'fleet', label: 'FLEET' },
      { id: 'command', label: 'COMMAND' },
      { id: 'layers', label: 'LAYERS' },
    ];
    const openTab = (id: 'fleet' | 'command' | 'layers') => {
      setSheetTab(id);
      setSheetHeight('tall');
    };
    return (
      <>
        <MapContextMenu build={buildMenu} />

        <button
          type="button"
          onClick={() => flyTo('ORBIT')}
          className="phone-orbit glass-pill pointer-events-auto absolute z-20 rounded-[99px] px-3 py-2 label-mono text-[9px]"
          style={{
            letterSpacing: '0.08em',
            top: 'calc(env(safe-area-inset-top) + 62px)',
            right: 'calc(var(--safe-r) + 8px)',
          }}
        >
          ◴ ORBIT
        </button>

        {/* The fleet stop/recall stays reachable on a phone too, where the
            command dock is a collapsed sheet — the exact case the stop must not
            be buried in. Top-left, clear of the orbit pill and the sheet, and
            stacked above the sheet so the typed-KILL confirm, which makes it
            taller, is never slid under a tall sheet. */}
        <div
          data-map-panel="emergency"
          className="pointer-events-auto absolute z-[35]"
          style={{ top: 'calc(env(safe-area-inset-top) + 62px)', left: 'calc(var(--safe-l) + 8px)' }}
        >
          <EmergencyControls />
        </div>

        {pendingGoto || pendingOrbit ? (
          <div
            className="phone-above-sheet pointer-events-none absolute inset-x-2 z-30 flex justify-center"
            style={{
              bottom: `calc(var(--mobile-nav-h) + env(safe-area-inset-bottom) + var(${
                sheetHeight === 'tall' ? '--sheet-tall' : '--sheet-peek'
              }) + 14px)`,
            }}
          >
            {gotoCard}
            {orbitCard}
          </div>
        ) : null}

        <BottomSheet
          height={sheetHeight}
          onHeight={setSheetHeight}
          header={
            <div className="flex gap-1 px-2 pb-1">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => openTab(tab.id)}
                  className="flex-1 rounded-[11px] py-2 label-mono text-[9px]"
                  style={{
                    background: sheetTab === tab.id ? 'oklch(1 0 0 / 0.14)' : 'transparent',
                    color: sheetTab === tab.id ? 'var(--foreground)' : 'var(--muted-foreground-3)',
                  }}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          }
        >
          {sheetTab === 'fleet' ? <FleetRail /> : null}
          {sheetTab === 'command' ? (
            <div className="p-2">
              <CommandDock gotoArmed={gotoArmed} setGotoArmed={setGotoArmed} follow={follow} setFollow={setFollow} runner={runner} />
            </div>
          ) : null}
          {sheetTab === 'layers' ? (
            <div className="h-full">
              <RightRail />
            </div>
          ) : null}
        </BottomSheet>
      </>
    );
  }

  return (
    <>
      <MapContextMenu build={buildMenu} />

      <div className="pointer-events-auto absolute left-1/2 top-[86px] z-10 -translate-x-1/2">
        <button
          type="button"
          onClick={() => flyTo('ORBIT')}
          className="glass-pill rounded-[99px] px-4 py-2 label-mono text-[9px]"
          style={{ letterSpacing: '0.1em' }}
        >
          ◴ RETURN TO ORBIT
        </button>
      </div>

      {/* Tablet: room for one rail at a time, so each is a drawer opened from
          these two pills. They sit in the row the drawers start below, so a
          drawer never covers its own close control. */}
      {isTablet ? (
        <>
          <div className="pointer-events-auto absolute left-[14px] top-[86px] z-30">
            <Pill
              aria-expanded={railDrawer === 'fleet'}
              onClick={() => toggleRailDrawer('fleet')}
              className={cn('hit-44', railDrawer === 'fleet' && 'bg-[oklch(1_0_0/0.18)]')}
            >
              {railDrawer === 'fleet' ? '◂ FLEET' : 'FLEET ▸'}
            </Pill>
          </div>
          <div className="pointer-events-auto absolute right-[14px] top-[86px] z-30">
            <Pill
              aria-expanded={railDrawer === 'detail'}
              onClick={() => toggleRailDrawer('detail')}
              className={cn('hit-44', railDrawer === 'detail' && 'bg-[oklch(1_0_0/0.18)]')}
            >
              {railDrawer === 'detail' ? 'DETAIL ▸' : '◂ DETAIL'}
            </Pill>
          </div>
        </>
      ) : null}

      {/* The fleet stop/recall. Always on screen in the tactical view — never
          folded into a dock — because it is the one control that has to be one
          reach away. Pinned in the free band right of the fleet rail (the
          rail's width plus its gutters, the same offset the command dock pads
          by) and below the orbit pill, clear of the copilot dock and command
          dock. On a tablet it sits at the left edge and steps right of the
          fleet drawer when that opens, above the drawers so neither covers it.
          data-map-panel keeps the selected aircraft from sliding under it. */}
      <div
        data-map-panel="emergency"
        className={cn(
          'pointer-events-auto absolute left-[calc(var(--rail-l-w)+26px)] top-[128px] z-10',
          isTablet && 'top-[136px] z-30',
          isTablet && railDrawer !== 'fleet' && 'left-[14px]',
        )}
      >
        <EmergencyControls />
      </div>

      {/* data-map-panel: useSelectionInView measures these and keeps the
          selected aircraft out from under them. */}
      {!isTablet || railDrawer === 'detail' ? (
        <div
          data-map-panel="right-rail"
          className="pointer-events-auto absolute right-[14px] top-[86px] bottom-[var(--tl-reserve)] z-10 tablet:top-[136px] tablet:z-20 tablet:max-w-[calc(100vw-28px)]"
        >
          <RightRail />
        </div>
      ) : null}

      <div
        className={cn(
          'pointer-events-none absolute inset-x-0 bottom-0 z-10 flex flex-col items-center justify-end gap-3 pb-[calc(var(--tl-reserve)+2px)] pl-[calc(var(--rail-l-w)+26px)] pr-[calc(var(--rail-r-w)+14px)]',
          isTablet && 'px-[14px]',
          isTablet && railDrawer === 'fleet' && 'pl-[calc(var(--rail-l-w)+26px)]',
          isTablet && railDrawer === 'detail' && 'pr-[calc(var(--rail-r-w)+26px)]',
        )}
        // An open drawer takes its width off the dock rather than covering it:
        // picking an aircraft in the fleet drawer and then commanding it is
        // the whole reason to open the drawer.
        style={
          {
            '--dock-inset':
              isTablet && railDrawer === 'fleet'
                ? 'calc(var(--rail-l-w) + 12px)'
                : isTablet && railDrawer === 'detail'
                  ? 'calc(var(--rail-r-w) + 12px)'
                  : '0px',
          } as CSSProperties
        }
      >
        {gotoCard}
        {orbitCard}

        <div data-map-panel="command-dock" className="pointer-events-auto max-w-full">
          <CommandDock gotoArmed={gotoArmed} setGotoArmed={setGotoArmed} follow={follow} setFollow={setFollow} runner={runner} />
        </div>
      </div>
    </>
  );
}

/** Command has no toast surface; repositioning feedback is the drone moving. */
const NO_MESSAGE = (): void => {};

/** Mounts the map layers and switches between the strategic and tactical HUD. */
function CommandContent(): ReactNode {
  const controller = useMapController();
  const lod = useMapStore((s) => s.lod);
  const flyRequest = useMapStore((s) => s.flyRequest);
  const entered = useWorkspaceStore((s) => s.entered);

  // First, so the recorded track of the sortie sits under the live breadcrumb
  // and everything else rather than over the aircraft it belongs to.
  useSortieTrackLayer();
  useDroneMarkers({ colorFor: teamColorFor });
  // Drafts are drawn here too. Command used to hide them, which meant a route
  // somebody had planned but not yet uploaded was invisible to the person
  // deciding what the fleet does next — the commander could not see that an
  // aircraft had a plan waiting at all. The layer already draws a draft dashed
  // and dimmed against the solid uploaded line, and the dock chips
  // "N WP · NOT UPLOADED", so the two claims stay distinguishable.
  useMissionLayer({ showDraft: true });
  useZoneLayer();
  useOverlayLayer();
  useImpactLayer();
  useThreatLayer();
  // The supply picture is a commander's constraint, not a quartermaster's
  // detail: what can be rearmed and where is the difference between a plan and
  // a wish. Same layer module the Supply screen mounts, so the depot and
  // forward-site symbols can never drift between the two screens.
  useSupplyLayer();
  // Attack geometry above the routes and zones it is planned against, for the
  // same reason Planning mounts it late. A target placed on Planning is a
  // decision the commander is accountable for; it must not be visible only on
  // the screen it was drawn on.
  useAttackLayer();
  // Contacts from other people's systems, above the geometry they are read
  // against. Same layer module Planning mounts, so an ADS-B or TAK contact can
  // never be visible to the planner and absent from the screen the strike is
  // commanded on. The layer feeds itself: a layer that renders data another
  // screen was supposed to load draws nothing at all when Command is opened
  // directly, which is the bug the mission fetch above already records.
  useExternalTrackLayer();
  // What each aircraft itself reports around it: ships, traffic, what it is
  // steering round. Beside the external contacts, and drawn so neither can be
  // read as one of ours.
  useSurroundingsLayer();
  // Carrier launch points and what is still aboard each boat. The executor
  // launches whether or not anyone is on Planning, so the commander sees it here.
  useCarrierLayer();
  useAorLayer();
  // Last, so where an aircraft has been sent is never buried under a route
  // line, a zone fill or a threat ring.
  useCommandTargetLayer();
  // Selecting a drone opens a panel that used to land on top of it.
  useSelectionInView();

  // The shared threat picture, pulled once on entry and kept for the layer.
  useEffect(() => {
    void useThreatStore.getState().loadThreats();
  }, []);

  // Command mounts the mission and zone layers but used to fetch neither, so
  // only a session that had been through Planning first had anything in the
  // store — land on Command directly (or hard-refresh there) and the routes
  // already flying on the aircraft drew nothing at all. Each layer must load
  // the data it renders rather than depending on another screen's visit.
  useEffect(() => {
    void missionActions.loadUploaded();
    // The server reads each aircraft's mission on connect, when its count
    // changes and every five minutes, so a mission loaded from another ground
    // station turns up in the record without anyone here asking. This picks
    // it up; a hidden tab skips the round rather than polling for nobody.
    const timer = setInterval(() => {
      if (!document.hidden) void missionActions.loadUploaded();
    }, MISSION_POLL_MS);
    void missionActions.loadZones();
    // Drafts live in the plans, not in the uploaded record, so the draft layer
    // draws nothing until they are loaded — the same "each layer loads the data
    // it renders" rule the uploaded routes above are here for.
    void missionActions.loadPlans();
    return () => clearInterval(timer);
  }, []);

  // The supply reference is slow-moving and shared with the Supply screen, so a
  // session that has already loaded it does not pay for it again.
  useEffect(() => {
    if (!useSupplyStore.getState().referenceLoaded) void useSupplyStore.getState().loadReference();
  }, []);

  // Clicking anywhere inside an AOR's dashed boundary is the same as clicking
  // its pill: the box looks like a door, so it is one.
  //
  // This used to bail whenever the operator had entered, which made the boxes
  // dead once you were inside any AOR — you could see a neighbouring box and
  // clicking it did nothing. It stays a door at every zoom; the guard that
  // matters is only that clicking inside the AOR you are already in must not
  // yank the camera back to its centre while you are working there.
  useEffect(
    () =>
      controller.onClick((point) => {
        const aor = aorForPosition(point.lat, point.lon);
        if (!aor) return;
        const workspace = useWorkspaceStore.getState();
        if (workspace.entered && workspace.aor === aor.id) return;
        useMapStore.getState().flyTo(aor.id);
      }),
    [controller],
  );

  // Publish LOD to the body so CSS can hide tactical markers on the globe.
  useEffect(() => {
    document.body.dataset.lod = lod;
    return () => {
      delete document.body.dataset.lod;
    };
  }, [lod]);

  // Sims can be dragged to reposition them here too, not just on Planning. Only
  // simulators move (the hook ignores real aircraft); on Command the drone
  // visibly sliding is the whole feedback, so no status message is needed.
  useSimDrag(controller, NO_MESSAGE);

  useEffect(() => {
    if (!flyRequest.target) return;
    const map = controller.raw();
    if (!map) return;
    if (flyRequest.target === 'ORBIT') {
      hasLanded = false;
      useWorkspaceStore.getState().setEntered(false);
      flyToOrbit(map);
    } else {
      hasLanded = true;
      useWorkspaceStore.getState().setAor(flyRequest.target);
      useWorkspaceStore.getState().setEntered(true);
      flyToAor(map, aorById(flyRequest.target));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flyRequest.nonce]);

  return (
    <>
      {/* What the pointer is over on the target, attack and supply layers.
          Outside the entered/orbit switch so they cannot be torn down
          mid-hover. A marker the commander cannot interrogate is a dot. */}
      <CommandTargetHoverLabel />
      <AttackHoverLabel />
      <SupplySiteHoverLabel />
      <ExternalTrackHoverLabel />
      <SurroundingsDetailCard />
      {entered ? <TacticalOverlay /> : <StrategicOverlay />}
    </>
  );
}

export function CommandView(): ReactNode {
  // The camera is seeded once, when the map is created. Land straight on the
  // tactical view if the operator has already flown in this session.
  const start = hasLanded ? aorById(useWorkspaceStore.getState().aor) : null;
  const camera = start
    ? { center: { lat: start.center[1], lon: start.center[0] }, zoom: start.zoom, pitch: start.pitch, bearing: 0 }
    : { center: { lat: ORBIT.center[1], lon: ORBIT.center[0] }, zoom: ORBIT.zoom, pitch: ORBIT.pitch, bearing: ORBIT.bearing };

  return (
    <MapCanvas
      center={camera.center}
      zoom={camera.zoom}
      pitch={camera.pitch}
      bearing={camera.bearing}
    >
      <CommandContent />
    </MapCanvas>
  );
}

export default CommandView;
